import axios from "axios"
import { useEffect, useState } from "react"
import { getId, getToken, usePermissions } from "../user/User"
import { toast } from "react-toastify"
import { Chart as ChartJS, ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend } from "chart.js"
import { Bar, Line, Pie } from "react-chartjs-2"
import { Link } from "react-router-dom"

const Dashboard = () => {
  ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Tooltip, Legend);

  // Data states
  const [clients, setClients] = useState([])
  const [meetings, setMeetings] = useState([])
  const [statusList, setStatusList] = useState([])
  const [tasks, setTasks] = useState([])
  const [monthlyGoal, setMonthlyGoal] = useState(0)
  const [serviceAgreementDeadline, setServiceAgreementDeadline] = useState(0)
  const [lowPrioTaskDeadlineInDays, setLowPrioTaskDeadlineInDays] = useState(0)
  const [mediumPrioTaskDeadlineInDays, setMediumPrioTaskDeadlineInDays] = useState(0)
  const [highPrioTaskDeadlineInDays, setHighPrioTaskDeadlineInDays] = useState(0)

  // Calculated metric states
  const [weeklyGoal, setWeeklyGoal] = useState(0)

  const [monthlyEarnings, setMonthlyEarnings] = useState(0)
  const [monthlyGoalPercentage, setMonthlyGoalPercentage] = useState(0)
  const [weeklyEarnings, setWeeklyEarnings] = useState(0)
  const [weeklyGoalPercentage, setWeeklyGoalPercentage] = useState(0)

  const [closeRate, setCloseRate] = useState(0)
  const [showUpRate, setShowUpRate] = useState(0)
  const [daysToSale, setTimeToSale] = useState(0)

  const [closeReasonChart, setCloseReasonChart] = useState(null)
  const [upcomingEvents, setUpcomingEvents] = useState([])

  const [monthlySells, setMonthlySells] = useState([])
  const [revenue, setRevenue] = useState([])
  const [revenueLabels, setRevenueLabels] = useState([])
  const [revenuePerSector, setRevenuePerSector] = useState([])
  const [revenuePerSectorLabels, setRevenuePerSectorLabels] = useState([])

  const isWithinLastYear = (inputDate) => {
    const oneYearAgo = new Date()
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
    return inputDate >= oneYearAgo
  }

  const isWithinLastWeek = (inputDate) => {
    const lastWeek = new Date()
    let date = lastWeek.getDate() - 7
    lastWeek.setDate(date)
    if(date < 0) {
      lastWeek.setDate(30 + date)
      lastWeek.setMonth(lastWeek.getMonth() - 1)
      if(lastWeek.getMonth() < 0) {
        lastWeek.setMonth(11)
        lastWeek.setFullYear(lastWeek.getFullYear() - 1)
      }
    }
    return inputDate >= lastWeek
  }

  function isSameMonth(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    return d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth();
  }

  function getRandomColor() {
    var letters = '0123456789ABCDEF'
    var color = '#'
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)]
    }
    return color
  }

  function filter(list, propertyKey, propertyValue) {
    return list.filter(entry => entry[propertyKey] === propertyValue)
  }

  function isDayWithinTheNextWeek(payDate) {
    const today = new Date()
    const targetDay = payDate.getDate()

    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)

      if (date.getDate() === targetDay) {
        return true
      }
    }

    return false
  }

  function getDaysDifference(smallDate, bigDate) {
    const msPerDay = 1000 * 60 * 60 * 24
    const utc1 = Date.UTC(smallDate.getFullYear(), smallDate.getMonth(), smallDate.getDate())
    const utc2 = Date.UTC(bigDate.getFullYear(), bigDate.getMonth(), bigDate.getDate())
    return Math.floor((utc2 - utc1) / msPerDay)
  }

  // Reminder if no contact (status_updated) in X days (based on status)
  function wasNotContactedRecently(client) {
    if(statusList.length == 0) {
      return false
    }
    const found = statusList.filter(statusInList => statusInList.name == client.status)
    if(found.length != 1) {
      console.log("Status " + client.status + " not found!")
      return false
    }

    if(getDaysDifference(new Date(client.status_updated), new Date()) >= found[0].remind_time_in_days) {
      return true
    }
    return false
  }

  function getDaysNotContacted(client) {
    if(statusList.length == 0) {
      return false
    }
    const found = statusList.filter(statusInList => statusInList.name == client.status)
    if(found.length != 1) {
      console.log("Status " + client.status + " not found!")
      return 0
    }

    return getDaysDifference(new Date(client.status_updated), new Date())
  }

  // Reminder service agreement is expiring (service_agreement_end) in X days (service_agreement_deadline_in_days)
  function serviceAgreementIsExpiringSoon(client) {
    if(!(client.status == "Sold" || client.status == "Done")) {
      return false
    }
    if(new Date(client.service_agreement_end) < new Date()) {
      return false
    }
    console.log("Service agreement diff " + client.company_name + ": " + getDaysDifference(new Date(), new Date(client.service_agreement_end)))
    if(getDaysDifference(new Date(), new Date(client.service_agreement_end)) <= serviceAgreementDeadline) {
      return true
    }
    return false
  }

  function getDaysUntilServiceAgreementExpiry(client) {
    return getDaysDifference(new Date(), new Date(client.service_agreement_end))
  }

  function getPaymentDate(date) {
    const payDate = new Date()
    payDate.setDate(date.getDate())
    let index = 1
    if (isNaN(payDate.getTime())) {
      payDate.setDate(date.getDate() - index)
      index++
    }
    return payDate.toLocaleDateString(undefined, { dateStyle: "medium" })
  }

  function todayMinusXDays(days) {
    const result = new Date();
    result.setDate(result.getDate() - days);
    return result;
  }

  function getPrioDeadline(prio) {
    if(prio === "High") {
      return highPrioTaskDeadlineInDays
    } else if(prio === "Medium") {
      return mediumPrioTaskDeadlineInDays
    } else {
      return lowPrioTaskDeadlineInDays
    }
  }

  // Fetch data
  const { permissions } = usePermissions(getId())
  useEffect(() => {
    if(permissions != null && permissions.client !== undefined && permissions.client) {
        axios.get(process.env.REACT_APP_API_URL + "/api/clients", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
          if(res.data === null || res.data === "") {
              return
          }
          setClients(res.data)
        }).catch(err => {
          if(axios.isCancel(err)) {
            return
          }
          console.error(err)
          toast.error("Unable to load clients!")
        })
        
        axios.get(process.env.REACT_APP_API_URL + "/api/clients/meetings", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
          if(res.data === null || res.data === "") {
              return
          }
          setMeetings(res.data)
        }).catch(err => {
          if(axios.isCancel(err)) {
            return
          }
          console.error(err)
          toast.error("Unable to load client meetings!")
        })

        axios.get(process.env.REACT_APP_API_URL + "/api/globalsettings", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
          if(res.data === null || res.data === "") {
              return
          }
          setMonthlyGoal(res.data.monthly_goal)
          setServiceAgreementDeadline(res.data.service_agreement_deadline_in_days)
          setLowPrioTaskDeadlineInDays(res.data.low_prio_task_deadline_in_days)
          setMediumPrioTaskDeadlineInDays(res.data.medium_prio_task_deadline_in_days)
          setHighPrioTaskDeadlineInDays(res.data.high_prio_task_deadline_in_days)
        }).catch(err => {
          if(axios.isCancel(err)) {
            return
          }
          console.error(err)
          toast.error("Unable to load global settings!")
        })

        axios.get(process.env.REACT_APP_API_URL + "/api/status", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
          if(res.data === null || res.data === "") {
              return
          }
          setStatusList(res.data)
        }).catch(err => {
          if(axios.isCancel(err)) {
            return
          }
          console.error(err)
          toast.error("Unable to load statuses!")
        })

        axios.get(process.env.REACT_APP_API_URL + "/api/tasks", {
            headers: {
                "Authorization": "Bearer " + getToken()
            }
        })
        .then(res => {
          if(res.data === null || res.data === "") {
              return
          }
          setTasks(res.data)
        }).catch(err => {
          if(axios.isCancel(err)) {
            return
          }
          console.error(err)
          toast.error("Unable to load tasks!")
        })
      }
    }, [permissions])
    
  // Calculate metrics
  useEffect(() => {
    let weeklyGoal = parseInt(monthlyGoal / (52 / 12))
    setWeeklyGoal(weeklyGoal)

    // Mapping Month (0-11/Jan->Dec) -> Earnings
    let incomePerSector = new Map()
    let incomePerMonth = [0,0,0,0,0,0,0,0,0,0,0,0]
    let weeklyEarnings = 0
    clients.forEach(client => {
      if(client.status != "Sold" && client.status != "Done") {
        return
      }
      let payingSince = new Date(client.initial_payment_date)
      
      // Monthly goal
      // Add initial_price
      if(isWithinLastYear(payingSince)) {
        let income = incomePerMonth[payingSince.getMonth()]
        if(income == null) {
          income = 0;
        }
        incomePerMonth[payingSince.getMonth()] = income + client.initial_price
      }

      let sectorIncome = incomePerSector.get(client.sector_name)
      if(sectorIncome == null) {
        sectorIncome = 0;
      }
      incomePerSector.set(client.sector_name, sectorIncome + client.initial_price)

      // Add monthly_price for last 12 months
      for (let monthIndexOffset = 0; monthIndexOffset <= 12; monthIndexOffset++) {
        const previousMonth = new Date()
        let newMonth = previousMonth.getMonth() - monthIndexOffset
        if(newMonth < 0) {
          newMonth = 12 + newMonth
          previousMonth.setFullYear(previousMonth.getFullYear() - 1)
        }
        previousMonth.setMonth(newMonth)
        
        if(previousMonth >= payingSince) {
          let income = incomePerMonth[previousMonth.getMonth()]
          if(income == null) {
            income = 0;
          }
          incomePerMonth[previousMonth.getMonth()] = income + client.monthly_price
          
          let sectorIncome = incomePerSector.get(client.sector_name)
          if(sectorIncome == null) {
            sectorIncome = 0;
          }
          incomePerSector.set(client.sector_name, sectorIncome + client.monthly_price)
        }
      }

      // Weekly goal
      if(isWithinLastWeek(payingSince)) {
        weeklyEarnings += client.initial_price
      }
      weeklyEarnings += client.monthly_price
    })

    let monthlyEarnings = incomePerMonth[(new Date()).getMonth()]
    setMonthlyEarnings(monthlyEarnings)
    setMonthlyGoalPercentage(Math.min(parseInt(monthlyEarnings / monthlyGoal * 100), 100))
    setWeeklyEarnings(weeklyEarnings)
    setWeeklyGoalPercentage(Math.min(parseInt(weeklyEarnings / weeklyGoal * 100), 100))

    let revenuePerSector = []
    let revenuePerSectorLabels = []
    for (const [sector, amount] of incomePerSector.entries()) {
      revenuePerSector.push(amount)
      revenuePerSectorLabels.push(sector)
    }
    setRevenuePerSector(revenuePerSector)
    setRevenuePerSectorLabels(revenuePerSectorLabels)

    let revenue = []
    let revenueLabels = []
    for(let i = 11; i >= 0; i--) {
      let date = new Date(new Date().getTime() - (i * 2592000000))
      revenue.push(incomePerMonth[date.getMonth()])
      revenueLabels.push(date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }))
    }
    setRevenue(revenue)
    setRevenueLabels(revenueLabels)

    let sold = 0
    let overall = 0
    clients.forEach(client => {
      if(!(client.status == "Sold" || client.status == "Done" || client.status == "Lost" || client.status == "NoShow")) {
        return
      }
      if(client.status == "Sold" || client.status == "Done") {
        sold++
      }
      overall++
    });
    setCloseRate(overall == 0 ? 100 : parseInt(sold / overall * 100))

    let showUp = 0
    meetings.forEach(meeting => {
      if(meeting.showup) {
        showUp++
      }
    });
    setShowUpRate(meetings.length == 0 ? 100 : parseInt(showUp / meetings.length * 100))

    let totalDays = 0;
    let soldCount = 0;
    clients.forEach(client => {
      if (client.status !== "Sold" && client.status !== "Done") return

      const daysToAdd = Math.round((new Date(client.initial_payment_date) - new Date(client.created)) / (1000 * 60 * 60 * 24))
      console.log(daysToAdd + " days for client " + client.company_name)
      totalDays += daysToAdd
      soldCount++
    });

    const timeToSale = soldCount > 0 ? (totalDays / soldCount).toFixed(0) : 0
    console.log("Time to sale: " + totalDays + "(days)/" + soldCount + "(sold)=" + timeToSale)
    setTimeToSale(timeToSale)

    let closeReasonLabels = [...new Set(clients.filter(client => client.status == "Lost" || client.status == "Sold" || client.status == "Done").map(client => client.status))]
    let closeReasonData = []
    let closeReasonColors = []
    closeReasonLabels.forEach((status) => {
      closeReasonData.push(filter(clients, "status", status).length)
      closeReasonColors.push(getRandomColor())
    })
    setCloseReasonChart(<Pie data={{
      labels: closeReasonLabels,
      datasets: [{
        data: closeReasonData,
        backgroundColor: closeReasonColors,
      }]
    }}/>)

    let upcomingEvents = clients
      .filter(client => client.status === "Sold" || client.status === "Done")
      .filter(client => isDayWithinTheNextWeek(new Date(client.initial_payment_date)))
      .map(client => ({
        date: getPaymentDate(new Date(client.initial_payment_date)),
        message: `Payment of ${client.monthly_price}€ for ${client.company_name} on ${getPaymentDate(new Date(client.initial_payment_date))}`,
        client: client,
      }))
    
    upcomingEvents.push(...clients
      .filter(client => wasNotContactedRecently(client))
      .map(client => ({
        date: todayMinusXDays(getDaysNotContacted(client)),
        message: `Client '${client.company_name}' was not contacted within the last ${getDaysNotContacted(client)} days`,
        client: client,
      })))
    
    upcomingEvents.push(...clients
      .filter(client => serviceAgreementIsExpiringSoon(client))
      .map(client => ({
        date: todayMinusXDays(getDaysUntilServiceAgreementExpiry(client)),
        message: `Service agreement of '${client.company_name}' is expiring in ${getDaysUntilServiceAgreementExpiry(client)} days`,
        client: client,
      })))

    // Oldest (important) first
    upcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
    console.log(upcomingEvents)

    const eventLimit = 15
    if(upcomingEvents > eventLimit) {
      upcomingEvents.slice(0, eventLimit)
    }

    setUpcomingEvents(upcomingEvents)

    let data = []
    for(let i = 11; i >= 0; i--) {
      let date = new Date(new Date().getTime() - (i * 2592000000))
      data.push({
        label: date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
        data: [clients.filter(client => isSameMonth(new Date(client.initial_payment_date), date)).length],
        backgroundColor: [getRandomColor()],
      })
    }
    setMonthlySells(data)
  }, [clients, meetings, monthlyGoal, statusList, serviceAgreementDeadline])

  return (
    <div className="dashboard">
      <div className="row">
        <div className="col-12 col-md-6 col-xxl-3">
          <div className="small-box" style={{height: "85%"}}>
            <div className="inner">
              <div className="progress-group">
                Monthly goal
                <span className="float-end"><b>{monthlyEarnings}€</b>/{monthlyGoal}€</span>
                <div className="progress progress-mb">
                  <div className={"progress-bar text-bg-" + (monthlyGoalPercentage >= 80 ? "success" : monthlyGoalPercentage >= 50 ? "warning" : "danger")} style={{ width: monthlyGoalPercentage + "%"}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-12 col-md-6 col-xxl-3">
          <div className="small-box" style={{height: "85%"}}>
            <div className="inner">
              <div className="progress-group">
                Weekly goal
                <span className="float-end"><b>{weeklyEarnings}€</b>/{weeklyGoal}€</span>
                <div className="progress progress-mb">
                  <div className={"progress-bar text-bg-" + (weeklyGoalPercentage >= 80 ? "success" : weeklyGoalPercentage >= 50 ? "warning" : "danger")} style={{ width: weeklyGoalPercentage + "%"}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 col-xxl-2">
          <div className="small-box">
            <div className="inner">
              <h3>{closeRate}<sup className="fs-5">%</sup></h3>
              <p>Close rate</p>
            </div>
            <i className="small-box-icon fa-solid fa-money-bill"></i>
          </div>
        </div>
        
        <div className="col-12 col-md-4 col-xxl-2">
          <div className="small-box">
            <div className="inner">
              <h3>{showUpRate}<sup className="fs-5">%</sup></h3>
              <p>Show-up rate</p>
            </div>
            <i className="small-box-icon fa-solid fa-eye"></i>
          </div>
        </div>
        
        <div className="col-12 col-md-4 col-xxl-2">
          <div className="small-box">
            <div className="inner">
              <h3>{daysToSale}<sup className="fs-5">days</sup></h3>
              <p>Average closing time</p>
            </div>
            <i className="small-box-icon fa-solid fa-clock"></i>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Close reason ratio</h3>
            </div>
            <div className="card-body">
              {closeReasonChart}
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Upcoming events</h3>
            </div>
            <div className="card-body">
              <ul>{upcomingEvents.map(event => <li key={event.message}><Link className="black" to={"/client/" + event.client.id}>{event.message}</Link></li>)}</ul>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Clients with unfinished checklists</h3>
            </div>
            <div className="card-body">
              <ul>{clients.filter(client => !client.checklist_done).map(client => <li key={client.id}><Link className="black" to={"/client/" + client.id}>{client.company_name + " (" + client.tasks.filter(task => !task.checked).length + " tasks)"}</Link></li>)}</ul>
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Task deadlines</h3>
            </div>
            <div className="card-body">
              <ul>{tasks.filter(task => task.due_date != "0001-01-01T00:00:00Z" &&
                new Date(task.due_date) > new Date() &&
                getDaysDifference(new Date(), new Date(task.due_date)) < getPrioDeadline(task.prio)
              ).map(task =>
                <li key={task.id}>
                  <Link className="black" to={"/client/" + task.client_id}>
                    {getDaysDifference(new Date(), new Date(task.due_date)) + " days left for: " + task.name + " (for " + task.client_name + ")"}
                  </Link>
                </li>
              )}</ul>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Websites sold</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: ['Websites sold'],
                datasets: monthlySells
              }}/>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Revenue</h3>
            </div>
            <div className="card-body">
              <Line data={{
                labels: revenueLabels,
                datasets: [
                  {
                    label: 'Revenue in €',
                    data: revenue,
                    backgroundColor: getRandomColor(),
                  }
                ]
              }}/>
            </div>
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Top performing sectors</h3>
            </div>
            <div className="card-body">
              <Bar data={{
                labels: revenuePerSectorLabels,
                datasets: [
                  {
                    label: 'Revenue in €',
                    data: revenuePerSector,
                    backgroundColor: getRandomColor(),
                  }
                ]
              }}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard